import { useState, useEffect, type FormEvent } from 'react'
import { useSupabase } from '@/hooks/useSupabase'
import { showToast } from '@/components/layout/Toast'
import type { Empresa } from '@/lib/types'

export default function Configuracion() {
  const db = useSupabase()
  const [empresa, setEmpresa] = useState<Empresa | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState<Omit<Empresa, 'id'>>({
    nombre: '', razon_social: '', ruc: '', telefono: '', email: '', website: '', ubicacion: '', logo_url: '',
    color_primario: '#1C4587', color_secundario: '#FFFFFF',
    banco: '', cuenta_corriente: '', cci: '', swift: '', direccion_banco: '',
  })

  useEffect(() => {
    (async () => {
      try {
        const data = await db.getAll<Empresa>('empresa')
        if (data.length > 0) { setEmpresa(data[0]); setForm({ ...data[0] }); delete (form as Record<string, unknown>).id }
      } catch { showToast('Error al cargar configuración', 'error') }
      finally { setLoading(false) }
    })()
  }, [])

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setSaving(true)
    try {
      if (empresa) {
        const { ...clean } = form; delete (clean as Record<string, unknown>).id
        await db.update('empresa', { id: empresa.id, ...clean })
        showToast('Configuración guardada', 'success')
      }
    } catch { showToast('Error al guardar', 'error') }
    finally { setSaving(false) }
  }

  const set = (key: keyof Omit<Empresa, 'id'>, val: string) => setForm(f => ({ ...f, [key]: val }))

  if (loading) return <div className="page-enter" style={{ padding: '2rem' }}><div className="page-header"><h1>Configuración</h1></div><div style={{ color: 'var(--text-secondary)', marginTop: '2rem', textAlign: 'center' }}><div className="auth-spinner" style={{ margin: '0 auto 12px' }} />Cargando...</div></div>

  return (
    <div className="page-enter">
      <div className="page-header">
        <div><h1>Configuración</h1><p className="page-subtitle">Datos de la empresa emisora de cotizaciones</p></div>
        <button className="btn btn-primary" onClick={submit} disabled={saving} id="btn-save-config">
          {saving ? 'Guardando...' : '💾 Guardar Cambios'}
        </button>
      </div>

      <form onSubmit={submit}>
        {/* General Info */}
        <div className="card" style={{ marginBottom: 24 }}>
          <div className="card-header"><h2>Datos Generales</h2></div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">Nombre Comercial</label><input className="form-input" value={form.nombre ?? ''} onChange={e => set('nombre', e.target.value)} /></div>
            <div className="form-group"><label className="form-label">Razón Social</label><input className="form-input" value={form.razon_social ?? ''} onChange={e => set('razon_social', e.target.value)} /></div>
          </div>
          <div className="form-row-3">
            <div className="form-group"><label className="form-label">RUC</label><input className="form-input" value={form.ruc ?? ''} onChange={e => set('ruc', e.target.value)} /></div>
            <div className="form-group"><label className="form-label">Teléfono</label><input className="form-input" value={form.telefono ?? ''} onChange={e => set('telefono', e.target.value)} /></div>
            <div className="form-group"><label className="form-label">Email</label><input className="form-input" type="email" value={form.email ?? ''} onChange={e => set('email', e.target.value)} /></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">Website</label><input className="form-input" value={form.website ?? ''} onChange={e => set('website', e.target.value)} /></div>
            <div className="form-group"><label className="form-label">Ubicación</label><input className="form-input" value={form.ubicacion ?? ''} onChange={e => set('ubicacion', e.target.value)} /></div>
          </div>
        </div>

        {/* Branding */}
        <div className="card" style={{ marginBottom: 24 }}>
          <div className="card-header"><h2>Branding</h2></div>
          <div className="form-row-3">
            <div className="form-group"><label className="form-label">Logo (URL)</label><input className="form-input" value={form.logo_url ?? ''} onChange={e => set('logo_url', e.target.value)} placeholder="logo.webp" /></div>
            <div className="form-group">
              <label className="form-label">Color Primario</label>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input type="color" value={form.color_primario ?? '#1C4587'} onChange={e => set('color_primario', e.target.value)} style={{ width: 40, height: 36, border: 'none', cursor: 'pointer', borderRadius: 6 }} />
                <input className="form-input" value={form.color_primario ?? ''} onChange={e => set('color_primario', e.target.value)} style={{ flex: 1 }} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Color Secundario</label>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input type="color" value={form.color_secundario ?? '#FFFFFF'} onChange={e => set('color_secundario', e.target.value)} style={{ width: 40, height: 36, border: 'none', cursor: 'pointer', borderRadius: 6 }} />
                <input className="form-input" value={form.color_secundario ?? ''} onChange={e => set('color_secundario', e.target.value)} style={{ flex: 1 }} />
              </div>
            </div>
          </div>
          {/* Preview */}
          <div style={{ marginTop: 16, padding: 20, borderRadius: 'var(--radius)', background: form.color_primario ?? '#1C4587', color: form.color_secundario ?? '#FFF', textAlign: 'center' }}>
            <strong style={{ fontFamily: 'Outfit, sans-serif', fontSize: '1.2rem' }}>{form.nombre || '1Conecta'}</strong>
            <p style={{ opacity: 0.8, fontSize: '0.85rem', marginTop: 4 }}>{form.razon_social || 'Vista previa del branding'}</p>
          </div>
        </div>

        {/* Banking */}
        <div className="card" style={{ marginBottom: 24 }}>
          <div className="card-header"><h2>Datos Bancarios</h2></div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">Banco</label><input className="form-input" value={form.banco ?? ''} onChange={e => set('banco', e.target.value)} /></div>
            <div className="form-group"><label className="form-label">Cuenta Corriente</label><input className="form-input" value={form.cuenta_corriente ?? ''} onChange={e => set('cuenta_corriente', e.target.value)} /></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">CCI</label><input className="form-input" value={form.cci ?? ''} onChange={e => set('cci', e.target.value)} /></div>
            <div className="form-group"><label className="form-label">SWIFT</label><input className="form-input" value={form.swift ?? ''} onChange={e => set('swift', e.target.value)} /></div>
          </div>
          <div className="form-group"><label className="form-label">Dirección del Banco</label><input className="form-input" value={form.direccion_banco ?? ''} onChange={e => set('direccion_banco', e.target.value)} /></div>
        </div>
      </form>
    </div>
  )
}
